

"use client";

import Link from "next/link";
import { useMemo, useState } from "react";

const CONTACT_EMAIL = "bonjour@jourjcreation.com";
const INSTAGRAM_URL = "https://instagram.com/jourjcreation";
const INSTAGRAM_HANDLE = "@jourjcreation";

const CONTACT_TOPICS = [
  {
    id: "commande",
    label: "Commande",
    eyebrow: "01",
    title: "Une question sur une commande",
    description: "Pour suivre une commande, demander une précision ou vérifier une disponibilité.",
    placeholder:
      "Bonjour, je vous contacte au sujet de ma commande / d’une référence vue sur la boutique...",
  },
  {
    id: "conseil",
    label: "Conseil",
    eyebrow: "02",
    title: "Besoin d’un conseil",
    description: "Pour choisir la bonne base, la bonne palette ou affiner un projet tricot/crochet.",
    placeholder:
      "Bonjour, j’aimerais être guidé(e) pour choisir une laine, une base ou une harmonie de couleurs...",
  },
] as const;

const FAQ_ITEMS = [
  {
    question: "Sous quel délai puis-je espérer une réponse ?",
    answer:
      "En général, une réponse est apportée sous 24 à 48 heures ouvrées. En période de mise en ligne de nouvelles collections, le délai peut être légèrement plus long.",
  },
  {
    question: "Puis-je demander de l’aide pour choisir une laine ?",
    answer:
      "Oui. Cette page est justement pensée pour ça : choix de base, envie de rendu, palette de couleurs, projet en tête… tout peut être précisé dans votre message.",
  },
  {
    question: "Est-il possible de parler d’une demande particulière ?",
    answer:
      "Oui, vous pouvez utiliser la section “Projet sur mesure” pour expliquer une envie précise, une idée cadeau, un assortiment ou une demande spéciale.",
  },
];

const monoFont = { fontFamily: '"IBM Plex Mono", monospace' };

const pagePalette = {
  background:
    "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
} as React.CSSProperties;

export default function ContactPage() {
  const [activeTopic, setActiveTopic] = useState<(typeof CONTACT_TOPICS)[number]["id"]>("commande");

  const currentTopic = useMemo(
    () => CONTACT_TOPICS.find((topic) => topic.id === activeTopic) ?? CONTACT_TOPICS[0],
    [activeTopic],
  );

  return (
    <main className="relative overflow-hidden text-[var(--foreground)]" style={pagePalette}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_14%_12%,rgba(217,90,47,0.12),transparent_20%),radial-gradient(circle_at_84%_14%,rgba(209,15,115,0.12),transparent_18%),radial-gradient(circle_at_82%_78%,rgba(217,90,47,0.10),transparent_18%)]" />
      <div className="pointer-events-none absolute -left-16 top-24 h-56 w-56 rounded-full bg-[rgba(217,90,47,0.14)] blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-20 h-72 w-72 rounded-full bg-[rgba(209,15,115,0.14)] blur-3xl" />  

      <section className="relative border-b border-[var(--border)]/80">
        <div className="mx-auto max-w-[1400px] px-6 py-10 sm:px-8 sm:py-12 lg:px-10 lg:py-14 xl:px-14">
          <div className="grid gap-8 lg:grid-cols-1 lg:items-end">
            <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
              <div className="flex items-center justify-center">
                <span style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/88">
                  Contact
                </span>
              </div>

              <h1 className="mt-6 max-w-[11ch] text-4xl leading-[0.94] sm:text-5xl lg:text-7xl">
                Parlons de votre projet laine.
              </h1>

              <p className="mt-6 max-w-[60ch] text-base leading-8 text-[var(--foreground)]/68 sm:text-lg">
                Une question sur une commande, une hésitation entre plusieurs teintes, une envie de
                composition ou un projet plus personnel ? Cette page a été pensée pour rendre la prise
                de contact douce, claire et agréable.
              </p>
            </div>

          </div>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-[1400px] px-6 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12 xl:px-14">
          <div className="grid gap-5 md:grid-cols-2">
            <ContactCard
              eyebrow="Email"
              title="Écrire directement"
              content={CONTACT_EMAIL}
              href={`mailto:${CONTACT_EMAIL}`}
              cta="Envoyer un message"
            />
            <ContactCard
              eyebrow="Instagram"
              title="Suivre l’atelier"
              content={INSTAGRAM_HANDLE}
              href={INSTAGRAM_URL}
              cta="Voir le compte"
            />
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-[1400px] px-6 pb-10 sm:px-8 lg:px-10 xl:px-14">
          <div className="grid gap-8 lg:grid-cols-[0.42fr_0.58fr] lg:items-start">
            <div className="space-y-6">
              <div>
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                  Parcours guidé
                </p>
                <h2 className="mt-3 text-3xl leading-[1.02] sm:text-4xl lg:text-5xl">
                  Choisissez le bon motif de contact avant d’écrire.
                </h2>
              </div>

              <p className="max-w-[48ch] text-base leading-8 text-[var(--foreground)]/68">
                Sélectionnez ce qui correspond le mieux à votre besoin. Le formulaire adapte ensuite
                son ton et son message d’introduction pour rendre la prise de contact plus simple.
              </p>

              <div className="space-y-3">
                {CONTACT_TOPICS.map((topic) => {
                  const active = topic.id === activeTopic;

                  return (
                    <button
                      key={topic.id}
                      type="button"
                      onClick={() => setActiveTopic(topic.id)}
                      className={[
                        "group w-full rounded-[26px] border p-5 text-left transition duration-300",
                        active
                          ? "border-[var(--primary)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] shadow-[0_16px_34px_rgba(209,15,115,0.12)]"
                          : "border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] hover:-translate-y-0.5 hover:border-[var(--primary)]",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/42">
                            {topic.eyebrow}
                          </p>
                          <h3 className="mt-3 text-2xl leading-tight">{topic.title}</h3>
                          <p className="mt-3 text-sm leading-7 text-[var(--foreground)]/66">
                            {topic.description}
                          </p>
                        </div>

                        <span
                          className={[
                            "mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl transition duration-300",
                            active
                              ? "bg-[var(--primary)] text-white"
                              : "bg-[rgba(217,90,47,0.14)] text-[var(--foreground)]/72 group-hover:bg-[rgba(217,90,47,0.20)]",
                          ].join(" ")}
                        >
                          →
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              id="formulaire-contact"
              className="rounded-[34px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[0_24px_60px_rgba(50,35,35,0.06)] sm:p-8 lg:p-10"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)]/75 pb-6">
                <div>
                  <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                    Formulaire
                  </p>
                  <h2 className="mt-3 text-3xl leading-tight sm:text-4xl">{currentTopic.title}</h2>
                </div>

                <span className="rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-4 py-2 text-sm text-[var(--foreground)]/68">
                  {currentTopic.label}
                </span>
              </div>

              <p className="mt-6 max-w-[60ch] text-base leading-8 text-[var(--foreground)]/68">
                {currentTopic.description}
              </p>

              <form className="mt-8 grid gap-5" action={`mailto:${CONTACT_EMAIL}`} method="post" encType="text/plain">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Prénom ou nom">
                    <input
                      name="Nom"
                      type="text"
                      placeholder="Votre nom"
                      className="h-14 w-full rounded-[20px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-5 text-sm outline-none transition duration-300 placeholder:text-[var(--foreground)]/34 focus:-translate-y-0.5 focus:border-[var(--primary)] focus:shadow-[0_12px_28px_rgba(255,63,142,0.08)]"
                    />
                  </Field>

                  <Field label="Email">
                    <input
                      name="Email"
                      type="email"
                      placeholder="vous@example.com"
                      className="h-14 w-full rounded-[20px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-5 text-sm outline-none transition duration-300 placeholder:text-[var(--foreground)]/34 focus:-translate-y-0.5 focus:border-[var(--primary)] focus:shadow-[0_12px_28px_rgba(255,63,142,0.08)]"
                    />
                  </Field>
                </div>

                <Field label="Sujet">
                  <input
                    name="Sujet"
                    type="text"
                    defaultValue={currentTopic.title}
                    className="h-14 w-full rounded-[20px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-5 text-sm outline-none transition duration-300 placeholder:text-[var(--foreground)]/34 focus:-translate-y-0.5 focus:border-[var(--primary)] focus:shadow-[0_12px_28px_rgba(255,63,142,0.08)]"
                  />
                </Field>

                <Field label="Message">
                  <textarea
                    name="Message"
                    rows={7}
                    defaultValue={currentTopic.placeholder}
                    className="w-full rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-5 py-4 text-sm leading-7 outline-none transition duration-300 placeholder:text-[var(--foreground)]/34 focus:-translate-y-0.5 focus:border-[var(--primary)] focus:shadow-[0_12px_28px_rgba(255,63,142,0.08)]"
                  />
                </Field>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <button
                    type="submit"
                    className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white shadow-[0_14px_30px_rgba(255,63,142,0.18)] transition duration-300 hover:-translate-y-0.5 hover:opacity-95"
                  >
                    Envoyer par email
                  </button>

                  <Link
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-6 py-3 text-sm font-medium text-[var(--foreground)] transition duration-300 hover:-translate-y-0.5 hover:border-[var(--accent)] hover:text-[var(--accent)]"
                  >
                    Ouvrir mon logiciel mail
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-t border-[var(--border)]/75">
        <div className="mx-auto max-w-[1400px] px-6 py-10 sm:px-8 lg:px-10 xl:px-14">
          <div className="grid gap-8 lg:grid-cols-[0.42fr_0.58fr]">
            <div>
              <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
                Questions fréquentes
              </p>
              <h2 className="mt-3 text-3xl leading-[1.02] sm:text-4xl lg:text-5xl">
                Quelques repères avant d’écrire.
              </h2>
            </div>

            <div className="space-y-4">
              {FAQ_ITEMS.map((item) => (
                <details
                  key={item.question}
                  className="group rounded-[26px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-5 shadow-[0_16px_34px_rgba(50,35,35,0.04)] transition duration-300 open:border-[var(--primary)]"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-medium text-[var(--foreground)] marker:content-none">
                    <span>{item.question}</span>
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(217,90,47,0.14)] text-xl text-[var(--foreground)]/72 transition duration-300 group-open:rotate-45 group-open:bg-[var(--primary)] group-open:text-white">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 max-w-[62ch] text-sm leading-7 text-[var(--foreground)]/66 sm:text-base">
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ContactCard({
  eyebrow,
  title,
  content,
  href,
  cta,
}: {
  eyebrow: string;
  title: string;
  content: string;
  href: string;
  cta: string;
}) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative overflow-hidden rounded-[30px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[0_18px_34px_rgba(50,35,35,0.04)] transition duration-500 hover:-translate-y-1.5 hover:border-[var(--primary)] hover:shadow-[0_24px_55px_rgba(50,35,35,0.08)] sm:p-7"
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(217,90,47,0.10),transparent_28%),radial-gradient(circle_at_84%_18%,rgba(209,15,115,0.08),transparent_20%)] opacity-80 transition duration-500 group-hover:scale-[1.04]" />

      <div className="relative z-[1] flex h-full flex-col">
      <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/86">
          {eyebrow}
        </p>
        <h3 className="mt-4 text-[2rem] leading-[1.02]">{title}</h3>
        <p className="mt-3 text-base leading-7 text-[var(--foreground)]/62">{content}</p>

        <div className="mt-8 flex items-center justify-between gap-4">
          <span className="text-sm text-[var(--foreground)]/42 transition duration-300 group-hover:text-[var(--foreground)]/62">
            {cta}
          </span>
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(217,90,47,0.14)] text-2xl text-[var(--foreground)] transition duration-300 group-hover:translate-x-1 group-hover:bg-[var(--primary)] group-hover:text-white">
            →
          </div>
        </div>
      </div>
    </Link>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-3">
      <span style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/52">
        {label}
      </span>
      {children}
    </label>
  );
}